import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Eye, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * /watch tour analytics widget.
 *
 * Reads aggregated views from /api/tts/tour-events/summary and groups them per
 * (ref, tab, lang). Helps sales see which prospects engaged with the share-link
 * tours and how far they got — segmented by language so EN-vs-ES interest is
 * visible at a glance.
 */
export default function TourAnalyticsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/tts/tour-events/summary");
      setRows(r.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // EN/ES totals
  const totalEn = rows.filter((r) => r.lang === "en").reduce((s, r) => s + r.views, 0);
  const totalEs = rows.filter((r) => r.lang === "es").reduce((s, r) => s + r.views, 0);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch (_) { return "—"; }
  };

  return (
    <section className="rounded-xl ink-card overflow-hidden" data-testid="tour-analytics-card">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--ink-border)] flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-white/50" />
          <h2 className="font-display font-bold text-lg">/watch tour engagement</h2>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5 text-white/60">
            <Globe className="h-3 w-3" /> EN <span className="font-mono-num text-white font-bold">{totalEn}</span>
          </div>
          <div className="flex items-center gap-1.5 text-white/60">
            <Globe className="h-3 w-3" /> ES <span className="font-mono-num text-white font-bold">{totalEs}</span>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="tour-analytics-refresh">
            Refresh
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="tour-analytics-table">
          <thead className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">
            <tr className="border-b border-[var(--ink-border)]">
              <th className="text-left px-6 py-3">Ref tag</th>
              <th className="text-left px-6 py-3">Track</th>
              <th className="text-left px-6 py-3">Lang</th>
              <th className="text-right px-6 py-3">Views</th>
              <th className="text-right px-6 py-3">Scenes seen</th>
              <th className="text-right px-6 py-3">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-6 py-6 text-white/40" colSpan={6}>Loading engagement…</td></tr>
            ) : error ? (
              <tr><td className="px-6 py-6 text-red-400" colSpan={6}>Failed to load: {error}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td className="px-6 py-6 text-white/40" colSpan={6}>No share-link views yet. Send a /watch?ref=… link to start tracking.</td></tr>
            ) : rows.map((r) => (
              <tr key={`${r.ref}-${r.tab}-${r.lang}`} className="border-b border-[var(--ink-border)] hover:bg-white/[0.02]" data-testid={`tour-analytics-row-${r.ref}`}>
                <td className="px-6 py-3 font-mono-num text-white">{r.ref}</td>
                <td className="px-6 py-3 text-white/70 capitalize">{r.tab}</td>
                <td className="px-6 py-3">
                  <span
                    className="text-[10px] tracking-[0.15em] uppercase font-bold px-2 py-0.5 rounded"
                    style={{
                      backgroundColor: r.lang === "es" ? "rgba(34,211,238,0.12)" : "rgba(255,255,255,0.06)",
                      color: r.lang === "es" ? "#67E8F9" : "rgba(255,255,255,0.7)",
                    }}
                  >
                    {r.lang}
                  </span>
                </td>
                <td className="px-6 py-3 text-right font-mono-num text-white">{r.views}</td>
                <td className="px-6 py-3 text-right font-mono-num text-white/70">{r.unique_scenes}</td>
                <td className="px-6 py-3 text-right text-xs text-white/50">{fmtDate(r.last)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
